import { ListItem } from '@common-fe/common-fe';

import { IClaimRepaymentDtoStatusEnum } from '@/dtos';
import { IRemainderDtoDestinationTypeEnum as DestinationType } from '@/types';
export { DestinationType };

import { REQUIRED_TEXT } from '@/common/constants';
import { Address, Option } from '@/common/types';

export enum LockboxStatus {
  NEW = 'NEW',
  PENDING = 'PENDING',
  DECLINED = 'DECLINED',
  APPROVED = 'APPROVED',
  PARTIALLY_APPROVED = 'PARTIALLY_APPROVED',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  INVESTMENTS_LIQUIDATED = 'INVESTMENTS_LIQUIDATED',
  CANCELED = 'CANCELED',
  DIVIDED = 'DIVIDED',
  PROCESSED_MANUALLY = 'PROCESSED_MANUALLY',
  VOID = 'VOID',
  REISSUE = 'REISSUE',
  PROCESS_CHECK = 'PROCESS_CHECK',
}

export enum LockboxType {
  INBOUND_TOA = 'INBOUND_TOA',
  OUTBOUND_TOA = 'OUTBOUND_TOA',
  RETURNED_CHECK = 'RETURNED_CHECK',
  CLAIM_REPAYMENT = 'CLAIM_REPAYMENT',
  BENEFICIARY = 'BENEFICIARY',
  HSA_FORM = 'HSA_FORM',
  EXCESS_CONTRIBUTION = 'EXCESS_CONTRIBUTION',
  COMBINE_HSA = 'COMBINE_HSA',
  MISTAKEN_DISTRIBUTION = 'MISTAKEN_DISTRIBUTION',
  HSA_ROLLOVER_FORM = 'HSA_ROLLOVER_FORM',
  HSA_CONTRIBUTION_FORM = 'HSA_CONTRIBUTION_FORM',
  HSA_TRANSFER_DIVORCE = 'HSA_TRANSFER_DIVORCE',
  HSA_ACCOUNT_CLOSURE = 'HSA_ACCOUNT_CLOSURE',
  DEATH_DISTRIBUTION = 'DEATH_DISTRIBUTION',
  POWER_OF_ATTORNEY = 'POWER_OF_ATTORNEY',
  RETURNED_MAIL = 'RETURNED_MAIL',
  UNKNOWN = 'UNKNOWN',
}

export enum EmployeeMatchingType {
  FULL_MATCH = 'FULL_MATCH',
  ACCOUNT_NOT_MATCHED = 'ACCOUNT_NOT_MATCHED',
  SSN_NOT_MATCHED = 'SSN_NOT_MATCHED',
  ACCOUNT_NOT_FOUND = 'ACCOUNT_NOT_FOUND',
  EMPLOYEE_NAME_NOT_MATCHED = 'EMPLOYEE_NAME_NOT_MATCHED',
  LINKED_EMPLOYEE = 'LINKED_EMPLOYEE',
  INVESTMENTS_TO_BE_LIQUIDATED = 'INVESTMENTS_TO_BE_LIQUIDATED',
  ADDRESS_NOT_MATCHED = 'ADDRESS_NOT_MATCHED',
  COMMUNICATION_NOT_MATCHED = 'COMMUNICATION_NOT_MATCHED',
}

export enum ClaimMatchingType {
  FULL_MATCH = 'FULL_MATCH',
  EMPLOYEE_NAME_NOT_MATCHED = 'EMPLOYEE_NAME_NOT_MATCHED',
}

export enum LockboxStatusReason {
  CHECK = 'CHECK',
  WIRE = 'WIRE',
  PARTICIPANT_NOT_FOUND = 'PARTICIPANT_NOT_FOUND',
  ACCOUNT_NOT_FOUND = 'ACCOUNT_NOT_FOUND',
  INVESTMENTS_LIQUIDATION_REQUIRED = 'INVESTMENTS_LIQUIDATION_REQUIRED',
  ACCOUNT_CLOSED = 'ACCOUNT_CLOSED',
  INSUFFICIENT_FUNDS = 'INSUFFICIENT_FUNDS',
  CLAIM_NOT_FOUND = 'CLAIM_NOT_FOUND',
  SEND_THE_REMAINDER_BACK = 'SEND_THE_REMAINDER_BACK',
  ALLOCATE_REMAINDER_TO_THE_ACCOUNT = 'ALLOCATE_REMAINDER_TO_THE_ACCOUNT',
  OTHER = 'OTHER',
}

export enum WireStatus {
  NOT_SENT = 'NOT_SENT',
  SENT = 'SENT',
}

export enum LockboxPaymentMethod {
  CHECK = 'CHECK',
  WIRE = 'WIRE',
}

export enum LockboxPaymentType {
  CHECK = 'CHECK',
  WIRE_SENT = 'WIRE_SENT',
  WIRE_NOT_SENT = 'WIRE_NOT_SENT',
}

export enum LockboxActiveType {
  PAYMENT = 'PAYMENT',
  NOPAYMENT = 'NOPAYMENT',
}

export enum LockboxSource {
  DOCUFREE = 'DOCUFREE',
}

interface LockboxOcrAddress {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
}

interface LockboxOcrDetailsAddress {
  line1?: string;
  city?: string;
  state?: string;
  zipcode?: string;
}

interface LockboxOcrDetails {
  address?: LockboxOcrDetailsAddress;
  originalMailingType?: string;
  checkNumber?: string;
  payTo?: string;
  amount?: number;
}

interface LockboxOcr {
  accountNumber?: string;
  employeeName?: string;
  ssn?: string;
  birthDate?: string;
  amount?: string;
  date?: string;
  address?: LockboxOcrAddress;
  claimIds?: string[];
  details?: LockboxOcrDetails;
}

export interface MatchedEmployee {
  employeeId?: string;
  employeeName?: string;
  ssn?: string;
  birthDate?: string;
  employmentStatus?: string;
  organizationId?: string;
  organizationName?: string;
  accountId?: string;
  accountNumber?: string;
  availableAmount?: number;
  investmentsBalance?: number;
  accountStatus?: string;
  investmentStatus?: string;
  labelTypes?: EmployeeMatchingType[];
  mailingAddress?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    zipcode?: string;
  };
}

export interface LockboxClaim {
  claimId?: string;
  employeeId?: string;
  employeeName?: string;
  organizationId?: string;
  organizationPath?: string;
  labelTypes?: ClaimMatchingType[];
  claimAmount?: number;
  availableForRepaymentAmount?: number;
  repaymentAmount?: string;
}

export enum LockboxCheckType {
  CLAIM = 'CLAIM',
  LOCKBOX = 'LOCKBOX',
}

export enum CheckStatusType {
  ISSUED = 'ISSUED',
  REISSUE_REQUIRED = 'REISSUE_REQUIRED',
}

export enum CheckMatchingType {
  FULL_MATCH = 'FULL_MATCH',
  AMOUNT_NOT_MATCHED = 'AMOUNT_NOT_MATCHED',
}

export interface LockboxCheck {
  checkId?: string;
  amount?: number;
  employeeName?: string;
  employeeId?: string;
  claimIds?: string[];
  issuedDate?: string;
  statusDate?: string;
  status?: string;
  payTo?: string;
  lockboxId?: string;
  checkType?: LockboxCheckType;
  paymentMethodId?: string;
  address?: Address;
  labelTypes?: CheckMatchingType[];
}

export interface MatchedEmployeePayload {
  employee_id?: number;
  claim_id?: number;
  employee_name?: string;
  ssn?: string;
  birth_date?: string;
  employment_status?: string;
  organization_id?: number;
  organization_name?: string;
  account_id?: number;
  account_number?: string;
  available_amount?: number;
  investments_balance?: number;
  account_status?: string;
  investment_status?: string;
  label_types?: EmployeeMatchingType[] & ClaimMatchingType[];
}

export interface ClaimRepaymentsPayload {
  claim_id?: number;
  repayment_amount?: number;
  employee_id?: number;
  organization_id?: number;
  organization_path?: string;
}

export interface CheckDetails {
  id?: string;
  vendorId?: string;
  vendorName?: string;
  checkName?: string;
  benefitOf?: string;
  payTo?: string;
  city?: string;
  state?: string;
  zip?: string;
  checkMemo?: string;
  addressLine1?: string;
  addressLine2?: string;
  undeliverable?: boolean;
}

export interface ContactDetails {
  contactId?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
}

export interface ChildRecord {
  id?: string;
  main?: boolean;
  name?: string;
  pages?: number[];
}

export enum ClaimSelectPlace {
  SUGGESTED_CLAIMS = 'SUGGESTED_CLAIMS',
  SEARCHING_CLAIMS = 'SEARCHING_CLAIMS',
  ALL_CLAIMS = 'ALL_CLAIMS',
}

export interface ClaimRepayment extends LockboxClaim {
  repaymentAmount?: string;
  selectPlace?: ClaimSelectPlace;
  status?: IClaimRepaymentDtoStatusEnum;
}

export interface Remainder {
  destinationType?: DestinationType;
  remainderAmount?: number;
  benefitOf?: string;
  payTo?: string;
  vendorName?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zip?: string;
  accountId?: string;
  undeliverable?: boolean;
}

export enum VoidReason {
  NOT_RECEIVED = 'NOT_RECEIVED',
  WRONG_ADDRESS = 'WRONG_ADDRESS',
  LOST_CHECK = 'LOST_CHECK',
  NO_LONGER_NEEDED = 'NO_LONGER_NEEDED',
  RETURNED_CHECK = 'RETURNED_CHECK',
}

export interface ProcessingDecision {
  type?: string;
  reason?: VoidReason;
  address?: Address;
  paymentMethodId?: string;
}

export interface LockboxDetails {
  type?: string;
  originalMailingType?: string;
  resendStatus?: string | null;
  processingAction?: string | null;
  processingDecision?: ProcessingDecision;
}

export interface Lockbox {
  id?: string;
  documentId?: string;
  name?: string;
  formName?: string;
  lockboxType?: LockboxType;
  lockboxStatus?: LockboxStatus;
  employeeName?: string;
  ssn?: string;
  birthDate?: string;
  accountNumber?: string;
  accountId?: string;
  amount?: string;
  contactId?: string;
  employeeId?: string;
  address?: string;
  requestDate?: string;
  matchEmployees?: MatchedEmployee[];
  ocr?: LockboxOcr;
  details?: LockboxDetails;
  checkDetails?: CheckDetails;
  contactDetails?: ContactDetails;
  reason?: LockboxStatusReason;
  notes?: string;
  updatedAt?: string;
  liquidateAllFunds?: boolean;
  parentId?: string;
  wireStatusType?: WireStatus;
  paymentMethodType?: LockboxPaymentMethod;
  archiveType?: LockboxActiveType;
  isMultipleRecordsWarning?: boolean;
  childRecords?: ChildRecord[];
  claimRepayments?: ClaimRepayment[];
  createdClaimRepayments?: ClaimRepayment[];
  suggestedClaims?: LockboxClaim[];
  remainder?: Remainder;
  checkId?: string;
  source?: string;
}

export interface LockboxListItem {
  id?: string;
  name?: string;
  employeeName?: string;
  amount?: number;
  accountNumber?: string;
  requestDate?: string;
  lockboxType?: LockboxType;
  lockboxStatus?: LockboxStatus;
}

export interface RepaymentMatching {
  employeeName: string;
  claimId: string;
  employeeId: string;
  organizationId: string;
  organizationPath: string;
  claimAmount: number;
  availableForRepaymentAmount: number;
}

export const LOCKBOX_STATUSES_OPTIONS: Option[] = [
  {
    key: LockboxStatus.NEW,
    value: LockboxStatus.NEW,
    title: 'New',
  },
  {
    key: LockboxStatus.PENDING,
    value: LockboxStatus.PENDING,
    title: 'Pending',
  },
  {
    key: LockboxStatus.DECLINED,
    value: LockboxStatus.DECLINED,
    title: 'Declined',
  },
  {
    key: LockboxStatus.DIVIDED,
    value: LockboxStatus.DIVIDED,
    title: 'Divided',
  },
  {
    key: LockboxStatus.APPROVED,
    value: LockboxStatus.APPROVED,
    title: 'Approved',
  },
  {
    key: LockboxStatus.PROCESSING,
    value: LockboxStatus.PROCESSING,
    title: 'Processing',
  },
  {
    key: LockboxStatus.INVESTMENTS_LIQUIDATED,
    value: LockboxStatus.INVESTMENTS_LIQUIDATED,
    title: 'Investments Liquidated',
  },
  {
    key: LockboxStatus.COMPLETED,
    value: LockboxStatus.COMPLETED,
    title: 'Completed',
  },
  {
    key: LockboxStatus.CANCELED,
    value: LockboxStatus.CANCELED,
    title: 'Canceled',
  },
  {
    key: LockboxStatus.PARTIALLY_APPROVED,
    value: LockboxStatus.PARTIALLY_APPROVED,
    title: 'Partially Approved',
  },
  {
    key: LockboxStatus.PROCESSED_MANUALLY,
    value: LockboxStatus.PROCESSED_MANUALLY,
    title: 'Processed Manually',
  },
];

export const lockboxTypeTitle = {
  [LockboxType.INBOUND_TOA]: 'Inbound TOA',
  [LockboxType.OUTBOUND_TOA]: 'Outbound TOA',
  [LockboxType.RETURNED_CHECK]: 'Returned Check',
  [LockboxType.UNKNOWN]: 'Unknown',
  [LockboxType.CLAIM_REPAYMENT]: 'Claim Repayment',
  [LockboxType.BENEFICIARY]: 'Beneficiary',
  [LockboxType.HSA_FORM]: 'HSA Form',
  [LockboxType.EXCESS_CONTRIBUTION]: 'Excess Contribution',
  [LockboxType.COMBINE_HSA]: 'Combine HSA',
  [LockboxType.MISTAKEN_DISTRIBUTION]: 'Mistaken Distribution',
  [LockboxType.HSA_ROLLOVER_FORM]: 'HSA Rollover Form',
  [LockboxType.HSA_CONTRIBUTION_FORM]: 'HSA Contribution Form',
  [LockboxType.HSA_TRANSFER_DIVORCE]: 'HSA Transfer Divorce',
  [LockboxType.HSA_ACCOUNT_CLOSURE]: 'HSA Account Closure',
  [LockboxType.DEATH_DISTRIBUTION]: 'Death Distribution',
  [LockboxType.POWER_OF_ATTORNEY]: 'Power of Attorney',
  [LockboxType.RETURNED_MAIL]: 'Returned Mail',
} as const;

export const voidReasonTitle = {
  [VoidReason.NOT_RECEIVED]: 'Not received',
  [VoidReason.WRONG_ADDRESS]: 'Wrong address',
  [VoidReason.LOST_CHECK]: 'Lost check',
  [VoidReason.NO_LONGER_NEEDED]: 'No longer needed',
  [VoidReason.RETURNED_CHECK]: 'Returned check',
} as const;

export const PAYMENT_LOCKBOX_TYPES: LockboxType[] = [
  LockboxType.INBOUND_TOA,
  LockboxType.CLAIM_REPAYMENT,
  LockboxType.MISTAKEN_DISTRIBUTION,
  LockboxType.HSA_ROLLOVER_FORM,
  LockboxType.HSA_CONTRIBUTION_FORM,
];

export const GENERIC_FORM_TYPES: LockboxType[] = [
  LockboxType.HSA_FORM,
  LockboxType.EXCESS_CONTRIBUTION,
  LockboxType.COMBINE_HSA,
  LockboxType.MISTAKEN_DISTRIBUTION,
  LockboxType.HSA_ROLLOVER_FORM,
  LockboxType.HSA_CONTRIBUTION_FORM,
  LockboxType.HSA_TRANSFER_DIVORCE,
  LockboxType.HSA_ACCOUNT_CLOSURE,
  LockboxType.DEATH_DISTRIBUTION,
  LockboxType.POWER_OF_ATTORNEY,
  LockboxType.RETURNED_CHECK,
  LockboxType.BENEFICIARY,
];

export const LOCKBOX_TYPES_OPTIONS: Option[] = [
  {
    key: LockboxType.INBOUND_TOA,
    value: LockboxType.INBOUND_TOA,
    title: lockboxTypeTitle[LockboxType.INBOUND_TOA],
  },
  {
    key: LockboxType.OUTBOUND_TOA,
    value: LockboxType.OUTBOUND_TOA,
    title: lockboxTypeTitle[LockboxType.OUTBOUND_TOA],
  },
  {
    key: LockboxType.RETURNED_CHECK,
    value: LockboxType.RETURNED_CHECK,
    title: lockboxTypeTitle[LockboxType.RETURNED_CHECK],
  },
  {
    key: LockboxType.CLAIM_REPAYMENT,
    value: LockboxType.CLAIM_REPAYMENT,
    title: lockboxTypeTitle[LockboxType.CLAIM_REPAYMENT],
  },
  {
    key: LockboxType.BENEFICIARY,
    value: LockboxType.BENEFICIARY,
    title: lockboxTypeTitle[LockboxType.BENEFICIARY],
  },
  {
    key: LockboxType.HSA_FORM,
    value: LockboxType.HSA_FORM,
    title: lockboxTypeTitle[LockboxType.HSA_FORM],
  },
  {
    key: LockboxType.EXCESS_CONTRIBUTION,
    value: LockboxType.EXCESS_CONTRIBUTION,
    title: lockboxTypeTitle[LockboxType.EXCESS_CONTRIBUTION],
  },
  {
    key: LockboxType.COMBINE_HSA,
    value: LockboxType.COMBINE_HSA,
    title: lockboxTypeTitle[LockboxType.COMBINE_HSA],
  },
  {
    key: LockboxType.MISTAKEN_DISTRIBUTION,
    value: LockboxType.MISTAKEN_DISTRIBUTION,
    title: lockboxTypeTitle[LockboxType.MISTAKEN_DISTRIBUTION],
  },
  {
    key: LockboxType.HSA_ROLLOVER_FORM,
    value: LockboxType.HSA_ROLLOVER_FORM,
    title: lockboxTypeTitle[LockboxType.HSA_ROLLOVER_FORM],
  },
  {
    key: LockboxType.HSA_CONTRIBUTION_FORM,
    value: LockboxType.HSA_CONTRIBUTION_FORM,
    title: lockboxTypeTitle[LockboxType.HSA_CONTRIBUTION_FORM],
  },
  {
    key: LockboxType.HSA_TRANSFER_DIVORCE,
    value: LockboxType.HSA_TRANSFER_DIVORCE,
    title: lockboxTypeTitle[LockboxType.HSA_TRANSFER_DIVORCE],
  },
  {
    key: LockboxType.HSA_ACCOUNT_CLOSURE,
    value: LockboxType.HSA_ACCOUNT_CLOSURE,
    title: lockboxTypeTitle[LockboxType.HSA_ACCOUNT_CLOSURE],
  },
  {
    key: LockboxType.DEATH_DISTRIBUTION,
    value: LockboxType.DEATH_DISTRIBUTION,
    title: lockboxTypeTitle[LockboxType.DEATH_DISTRIBUTION],
  },
  {
    key: LockboxType.POWER_OF_ATTORNEY,
    value: LockboxType.POWER_OF_ATTORNEY,
    title: lockboxTypeTitle[LockboxType.POWER_OF_ATTORNEY],
  },
  {
    key: LockboxType.RETURNED_MAIL,
    value: LockboxType.RETURNED_MAIL,
    title: lockboxTypeTitle[LockboxType.RETURNED_MAIL],
  },
  {
    key: LockboxType.UNKNOWN,
    value: LockboxType.UNKNOWN,
    title: lockboxTypeTitle[LockboxType.UNKNOWN],
  },
];

export const lockboxPaymentTypeTitle = {
  [LockboxPaymentType.CHECK]: 'Check',
  [LockboxPaymentType.WIRE_SENT]: 'Wire sent',
  [LockboxPaymentType.WIRE_NOT_SENT]: 'Wire not sent',
} as const;

export const LOCKBOX_PAYMENT_TYPES_OPTIONS: Option[] = [
  {
    key: LockboxPaymentType.CHECK,
    value: LockboxPaymentType.CHECK,
    title: lockboxPaymentTypeTitle[LockboxPaymentType.CHECK],
  },
  {
    key: LockboxPaymentType.WIRE_SENT,
    value: LockboxPaymentType.WIRE_SENT,
    title: lockboxPaymentTypeTitle[LockboxPaymentType.WIRE_SENT],
  },
  {
    key: LockboxPaymentType.WIRE_NOT_SENT,
    value: LockboxPaymentType.WIRE_NOT_SENT,
    title: lockboxPaymentTypeTitle[LockboxPaymentType.WIRE_NOT_SENT],
  },
];

export enum LockboxValidationError {
  AMOUNT_REQUIRED = 'AMOUNT_REQUIRED',
  AMOUNT_EXCEEDS_AVAILABLE_BALANCE = 'AMOUNT_EXCEEDS_AVAILABLE_BALANCE',
  AVAILABLE_BALANCE_MUST_BE_GREATER_THAN_ZERO = 'AVAILABLE_BALANCE_MUST_BE_GREATER_THAN_ZERO',
  EMPLOYEE_REQUIRED = 'EMPLOYEE_REQUIRED',
  INVESTMENTS_MUST_BE_LIQUIDATED = 'INVESTMENTS_MUST_BE_LIQUIDATED',
  VENDOR_REQUIRED = 'VENDOR_REQUIRED',
  HSA_ACCOUNT_REQUIRED = 'HSA_ACCOUNT_REQUIRED',
  HSA_ACCOUNT_CLOSED = 'HSA_ACCOUNT_CLOSED',
  HSA_ACCOUNT_SHOULD_BE_OPEN = 'HSA_ACCOUNT_SHOULD_BE_OPEN',
  TYPE_REQUIRED = 'TYPE_REQUIRED',

  VENDOR_NAME_REQUIRED = 'VENDOR_NAME_REQUIRED',
  BENEFIT_OF_REQUIRED = 'BENEFIT_OF_REQUIRED',
  ADDRESS_LINE_1_REQUIRED = 'ADDRESS_LINE_1_REQUIRED',
  CITY_REQUIRED = 'CITY_REQUIRED',
  STATE_REQUIRED = 'STATE_REQUIRED',
  ZIP_REQUIRED = 'ZIP_REQUIRED',

  RECORDS_CANNOT_BE_EMPTY = 'RECORDS_CANNOT_BE_EMPTY',
  MINIMUM_TWO_RECORDS_REQUIRED = 'MINIMUM_TWO_RECORDS_REQUIRED',
  ALL_PAGES_MUST_BE_SELECTED = 'ALL_PAGES_MUST_BE_SELECTED',

  CLAIM_REQUIRED = 'CLAIM_REQUIRED',
  CLAIMS_MUST_HAVE_THE_SAME_EMPLOYEE = 'CLAIMS_MUST_HAVE_THE_SAME_EMPLOYEE',
  NEED_TO_ALLOCATE_CLAIM = 'NEED_TO_ALLOCATE_CLAIM',
  TOO_MUCH_ALLOCATED = 'TOO_MUCH_ALLOCATED',
  CHECK_ADDRESS_REQUIRED = 'CHECK_ADDRESS_REQUIRED',
  ACCOUNT_REQUIRED = 'ACCOUNT_REQUIRED',
  TOTAL_ALLOCATED_EXCEEDED = 'TOTAL_ALLOCATED_EXCEEDED',
  FORM_NAME_REQUIRED = 'FORM_NAME_REQUIRED',
  REMAINDER_ADDRESS_UNDELIVERABLE = 'REMAINDER_ADDRESS_UNDELIVERABLE',
}

export enum LockboxServerErrorText {
  INVESTMENTS_MUST_BE_LIQUIDATED = 'Account cannot be closed as some investments operations are not settled yet',
}

export const lockboxValidationErrorTitle = {
  [LockboxValidationError.AMOUNT_REQUIRED]: 'Amount is required',
  [LockboxValidationError.AMOUNT_EXCEEDS_AVAILABLE_BALANCE]: 'The amount cannot exceed the available balance',
  [LockboxValidationError.AVAILABLE_BALANCE_MUST_BE_GREATER_THAN_ZERO]:
    'Available balance must be greater than $0.00',
  [LockboxValidationError.EMPLOYEE_REQUIRED]: 'Need to select an employee',
  [LockboxValidationError.INVESTMENTS_MUST_BE_LIQUIDATED]:
    'Account can’t be closed as some investments operations are not settled yet',
  [LockboxValidationError.HSA_ACCOUNT_REQUIRED]: 'HSA account must be present',
  [LockboxValidationError.VENDOR_REQUIRED]: 'Vendor is required',
  [LockboxValidationError.HSA_ACCOUNT_CLOSED]: 'HSA account can’t be closed',
  [LockboxValidationError.TYPE_REQUIRED]: 'Type is required',
  [LockboxValidationError.HSA_ACCOUNT_SHOULD_BE_OPEN]: 'HSA account should be open',

  [LockboxValidationError.VENDOR_NAME_REQUIRED]: 'Vendor name field is required',
  [LockboxValidationError.BENEFIT_OF_REQUIRED]: 'For benefit of field is required',
  [LockboxValidationError.ADDRESS_LINE_1_REQUIRED]: 'Address line 1 field is required',
  [LockboxValidationError.CITY_REQUIRED]: 'City field is required',
  [LockboxValidationError.STATE_REQUIRED]: 'State field is required',
  [LockboxValidationError.ZIP_REQUIRED]: 'ZIP code field is required',

  [LockboxValidationError.RECORDS_CANNOT_BE_EMPTY]: 'Records cannot be empty, please add at least one page',
  [LockboxValidationError.MINIMUM_TWO_RECORDS_REQUIRED]: 'At least two records must be created',
  [LockboxValidationError.ALL_PAGES_MUST_BE_SELECTED]: 'All pages must be selected',

  [LockboxValidationError.CLAIM_REQUIRED]: 'At least one claim must be selected',
  [LockboxValidationError.CLAIMS_MUST_HAVE_THE_SAME_EMPLOYEE]:
    'You can only select claims for one employee at a time',
  [LockboxValidationError.NEED_TO_ALLOCATE_CLAIM]: 'Need to allocate',
  [LockboxValidationError.TOO_MUCH_ALLOCATED]: 'Too much allocated',
  [LockboxValidationError.CHECK_ADDRESS_REQUIRED]: 'Address is required to decline this record',
  [LockboxValidationError.ACCOUNT_REQUIRED]: REQUIRED_TEXT,
  [LockboxValidationError.TOTAL_ALLOCATED_EXCEEDED]:
    'The sum of allocated amounts must not exceed the check amount',
  [LockboxValidationError.FORM_NAME_REQUIRED]: 'Form name is required',
  [LockboxValidationError.REMAINDER_ADDRESS_UNDELIVERABLE]: 'Address is undeliverable, please update it',
} as const;

export const lockboxStatusTitle = {
  [LockboxStatus.NEW]: 'New',
  [LockboxStatus.PENDING]: 'Pend',
  [LockboxStatus.DECLINED]: 'Decline',
  [LockboxStatus.APPROVED]: 'Approve',
  [LockboxStatus.PARTIALLY_APPROVED]: 'Partially approve',
  [LockboxStatus.PROCESSING]: 'Process',
  [LockboxStatus.COMPLETED]: 'Complete',
  [LockboxStatus.INVESTMENTS_LIQUIDATED]: 'Liquidate investments',
  [LockboxStatus.CANCELED]: 'Cancel',
  [LockboxStatus.DIVIDED]: 'Divide',
  [LockboxStatus.PROCESSED_MANUALLY]: 'Processed manually',
  [LockboxStatus.VOID]: 'Void check',
  [LockboxStatus.REISSUE]: 'Reissue check',
  [LockboxStatus.PROCESS_CHECK]: 'Process',
} as const;

export const RETURN_MAIL_PROCESSED_STATUS_TITLE = 'Processed (marked as bad address)';
export const PROCESSED_MANUALLY_STATUS_TITLE = 'Processed Manually';

export const lockboxReasonTitle = {
  [LockboxStatusReason.CHECK]: 'Check',
  [LockboxStatusReason.WIRE]: 'Wire',
  [LockboxStatusReason.PARTICIPANT_NOT_FOUND]: 'Participant cant be found',
  [LockboxStatusReason.ACCOUNT_NOT_FOUND]: 'No account found',
  [LockboxStatusReason.INVESTMENTS_LIQUIDATION_REQUIRED]: 'Investments must be liquidated',
  [LockboxStatusReason.ACCOUNT_CLOSED]: 'Account closed',
  [LockboxStatusReason.INSUFFICIENT_FUNDS]: 'Insufficient funds',
  [LockboxStatusReason.CLAIM_NOT_FOUND]: 'Claim can’t be found',
  [LockboxStatusReason.SEND_THE_REMAINDER_BACK]: 'Send the remainder back',
  [LockboxStatusReason.ALLOCATE_REMAINDER_TO_THE_ACCOUNT]: 'Allocate remainder to the account',
  [LockboxStatusReason.OTHER]: 'Other',
} as const;

export const lockboxWireStatusTitle = {
  [WireStatus.SENT]: 'Sent',
  [WireStatus.NOT_SENT]: 'To be sent',
};

export interface EmployeeCardsListItem extends ListItem {
  warnings?: EmployeeMatchingType[];
}

export interface ClaimCardsListItem extends ListItem {
  warnings?: ClaimMatchingType[];
}

export interface CheckCardsListItem extends ListItem {
  warnings?: CheckMatchingType[];
}

export const DIVIDING_MODE_GROUP_PREFIX = 'Secondary Record';
export const DIVIDING_MODE_MAIN_GROUP_KEY = 'main';
